import { asWidgetProps } from 'Src/types/widget-helpers';
import type { CSSProperties, ComponentProps } from 'react';
import React from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import ratioUtils from 'Components/utils/ratios/ratios.utils';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { InspirationalGridFancy } from './layouts/InspirationalGridFancy';
import { InspirationalGridFull } from './layouts/InspirationalGridFull';
import { InspirationalGridVertical } from './layouts/InspirationalGridVertical';
import { InspirationalGridHorizontal } from './layouts/InspirationalGridHorizontal';
import { InspirationalGridMainStage } from './layouts/InspirationalGridMainStage';
import { InspirationalDescription } from './InspirationalDescription';
import { InspirationalPicture } from './InspirationalPicture';
export const HORIZONTAL_RATIO = ratioUtils.XL;
export const VERTICAL_RATIO = ratioUtils.R_65_BY_48;
export const MAINSTAGE_CONTAINER_RATIO = 480 / 1180;
export const HORIZONTAL_CONTAINER_RATIO = 380 / 1180;
export const VERTICAL_CONTAINER_RATIO = MAINSTAGE_CONTAINER_RATIO;
export const FULL_CONTAINER_RATIO = 365 / 1440;
export const FANCY_CONTAINER_RATIO = 620 / 1440;
export const getContainerStyles = (ratio: string | number) => ({
  paddingBottom: `calc(${ratio} * 100%)`,
});
export type InspirationalPictureType = {
  url: string;
  title?: string;
  subtitle?: string;
  link?: string;
};
export type InspirationalGridProps = {
  layout?: 'mainStage' | 'horizontal' | 'vertical' | 'full' | 'fancy';
  pictures: Array<InspirationalPictureType>;
  title?: string;
  subtitle?: string;
  description?: string;
};
export type InspirationalPictureComponentType = InspirationalPictureType & {
  descriptionStyle?: CSSProperties;
  ratioKey?: string;
  className?: string;
  index?: number;
  isWide?: boolean;
  radius?: number;
  ratioDisplay?: number;
  imgClassName?: string;
  disableRelativePadding?: boolean;
};
const getComponentFromLayout = (layout) => {
  switch (layout) {
    case 'mainStage':
      return InspirationalGridMainStage;
    case 'horizontal':
      return InspirationalGridHorizontal;
    case 'vertical':
      return InspirationalGridVertical;
    case 'full':
      return InspirationalGridFull;
    case 'fancy':
      return InspirationalGridFancy;
    default:
      return InspirationalGridMainStage;
  }
};
export const InspirationalGrid = ({
  layout,
  pictures,
  title,
  subtitle,
  description,
}: InspirationalGridProps) => {
  const {
    isBrowserGreaterThan = {
      XS: true,
    },
  } = useResponsive();
  const numberOfPictures = pictures.length;
  let actualLayout = layout;
  if (numberOfPictures === 2) {
    actualLayout = 'mainStage';
  } else if (numberOfPictures >= 4) {
    actualLayout = 'fancy';
  }
  const TagName = getComponentFromLayout(actualLayout);
  const shouldShowDescription =
    numberOfPictures === 2 || numberOfPictures === 4 || actualLayout === 'horizontal';
  const editorialProps =
    shouldShowDescription || !isBrowserGreaterThan.XS
      ? {
          title,
          subtitle,
          description,
        }
      : {};
  return isBrowserGreaterThan.XS ? (
    <div className="direction-ltr">
      {/*
       I could have made rtl variants of the layouts but decided not to, as
       it would complicate the component's layout even more than it already is
       */}
      <SiteContainer
        {...asWidgetProps<ComponentProps<typeof SiteContainer>>({
          isWide: actualLayout === 'full' || actualLayout === 'fancy',
        })}
      >
        <TagName
          {...asWidgetProps<ComponentProps<typeof TagName>>({
            pictures: pictures,
            ...editorialProps,
          })}
        />
      </SiteContainer>
    </div>
  ) : (
    <SiteContainer
      {...asWidgetProps<ComponentProps<typeof SiteContainer>>({
        isWide: true,
      })}
    >
      <div className="px-20 pb-40">
        <InspirationalDescription
          {...asWidgetProps<ComponentProps<typeof InspirationalDescription>>(editorialProps)}
        />
      </div>
      <div className="scroll-x scrollbar-hidden flex snap-x snap-mandatory">
        {pictures.map((picture, index) => (
          <div
            className={classnames('w-[80vw] shrink-0 snap-start ps-15', {
              'me-15': index === pictures.length - 1,
            })}
            key={index}
          >
            <InspirationalPicture
              {...asWidgetProps<ComponentProps<typeof InspirationalPicture>>({
                ...picture,
                ratioKey: VERTICAL_RATIO,
                ratioDisplay: 0.8,
                disableRelativePadding: true,
              })}
            />
          </div>
        ))}
      </div>
    </SiteContainer>
  );
};
